import React, { useEffect, useState } from 'react'
import { View, Text } from 'react-native'
import { useParams } from 'react-router-dom'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { Timestamp, getFirestore, collection, doc, writeBatch } from 'firebase/firestore'
import { Casinos, Promotions, Vouchers } from './cs-connect'
import { useCasinoContext } from './containers/CasinoContextProvider'
import { Alert } from './utils/Alert.web'
import moment from 'moment'

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz'
const CODE_LENGTH = 6
const DOMAIN_SIZE = Math.pow(ALPHABET.length, CODE_LENGTH) // 36^6 = 2,176,782,336
const BATCH_SPACING = 1000000 // headroom per batch so a single run's indexes never wrap into the next second's window
const SCRAMBLE_MULTIPLIER = 1000003 // coprime with DOMAIN_SIZE (2^12 * 3^12), so the affine map below is a bijection
const SCRAMBLE_OFFSET = 123456789
const WRITE_CHUNK_SIZE = 500 // Firestore's writeBatch limit

// (a*x + b) mod n is a bijection on [0, n) whenever gcd(a, n) === 1, so distinct
// slots can never scramble to the same code.
function scramble(value: number) {
   return (value * SCRAMBLE_MULTIPLIER + SCRAMBLE_OFFSET) % DOMAIN_SIZE
}

function encodeBase36(value: number, length: number) {
   let remaining = value
   let result = ''
   for (let i = 0; i < length; i++) {
      result = ALPHABET[remaining % ALPHABET.length] + result
      remaining = Math.floor(remaining / ALPHABET.length)
   }
   return result
}

function generateVoucherCode(timestampSeconds: number, index: number) {
   const slot = (timestampSeconds * BATCH_SPACING + index) % DOMAIN_SIZE
   return encodeBase36(scramble(slot), CODE_LENGTH)
}

type GeneratedVoucher = Partial<Vouchers.Voucher> & { code: string }

export default function VoucherGenerator() {

   const { promotionId } = useParams<{ promotionId: string }>()
   const { casino } = useCasinoContext()
   const [promotion, setPromotion] = useState<Promotions.Promotion>()
   const [name, setName] = useState('')
   const [count, setCount] = useState('100')
   const [vouchers, setVouchers] = useState<GeneratedVoucher[]>([])
   const [isSaving, setIsSaving] = useState(false)

   useEffect(() => {
      fetchPromotion()
   }, [])

   return (
      <View style={{
         padding: 20
      }}>
         <Text style={{
            marginBottom: 15,
            fontSize: 18,
            fontWeight: 'bold'
         }}>Voucher Generator</Text>
         <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 20
         }}>
            <TextField
               label='Voucher Name'
               size='small'
               value={name}
               onChange={(e) => setName(e.target.value)} />
            <TextField
               style={{
                  marginLeft: 15
               }}
               label='Number of unique vouchers'
               type='number'
               size='small'
               value={count}
               onChange={(e) => setCount(e.target.value)} />
            <Button
               style={{
                  marginLeft: 15
               }}
               variant='contained'
               onClick={onClickGenerate}>Generate</Button>
            <Button
               style={{
                  marginLeft: 15
               }}
               variant='contained'
               color='secondary'
               disabled={vouchers.length === 0 || casino == null || promotion == null || isSaving}
               onClick={onClickBulkAdd}>{isSaving ? 'Saving...' : 'Save'}</Button>
         </View>
         {vouchers.length > 0 && (
            <Paper variant='outlined'>
               <Table size='small'>
                  <TableHead>
                     <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Id</TableCell>
                        <TableCell>Created At</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {vouchers.map((voucher) => (
                        <TableRow key={voucher.code}>
                           <TableCell>{voucher.code.toUpperCase()}</TableCell>
                           <TableCell>{voucher.name}</TableCell>
                           <TableCell>{voucher.id ?? '—'}</TableCell>
                           <TableCell>{moment(voucher.createdAt?.toDate()).format('DD MMM YYYY • hh:mm:ss A')}</TableCell>
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
            </Paper>
         )}
      </View>
   )

   async function fetchPromotion() {
      try {
         const response = await Promotions.getPromotionWithId(promotionId)
         if (response != null) {
            setPromotion(response)
         } else {
            throw new Error("We could not find this promotion");
         }
      } catch (error) {
         console.log('Error Getting Promotion', error)
         Alert.alert('Error Getting Promotion', String(error))
      }
   }

   function onClickGenerate() {
      const total = parseInt(count, 10)
      if (!total || total <= 0) return

      if (!name.trim()) {
         Alert.alert('Error Generating Vouchers', 'Voucher Name is required')
         return
      }

      const timestamp = Timestamp.now()

      const generated: GeneratedVoucher[] = []
      for (let index = 0; index < total; index++) {
         generated.push({
            code: generateVoucherCode(timestamp.seconds, index),
            name: name.trim(),
            createdAt: timestamp,
         })
      }
      setVouchers(generated)
   }

   async function onClickBulkAdd() {
      if (casino == null || promotion == null) {
         Alert.alert('Error Adding Vouchers', 'Casino and Promotion could not be found')
         return
      }

      setIsSaving(true)
      try {
         const firestore = getFirestore()
         const vouchersCollection = collection(firestore, `casinos/${casino.id}/promotions/${promotionId}/vouchers`)
         const saved = [...vouchers]

         for (let i = 0; i < saved.length; i += WRITE_CHUNK_SIZE) {
            const batch = writeBatch(firestore)
            const chunk = saved.slice(i, i + WRITE_CHUNK_SIZE)
            chunk.forEach((voucher) => {
               const docRef = doc(vouchersCollection)
               const newVoucher = {
                  id: docRef.id,
                  code: voucher.code,
                  name: voucher.name,
                  casinoInfo: Casinos.getCasinoInfo(casino),
                  promotionInfo: Promotions.getPromotionInfo(promotion),
                  createdAt: voucher.createdAt,
                  updatedAt: voucher.createdAt
               } as Vouchers.Voucher
               batch.set(docRef, newVoucher, { merge: false })
               voucher.id = docRef.id
            })
            await batch.commit()
         }

         setVouchers(saved)
         downloadCodesCsv(saved)
      } catch (error) {
         console.log('Error Adding Vouchers', error)
         Alert.alert('Error Adding Vouchers', String(error))
      }
      setIsSaving(false)
   }

   function downloadCodesCsv(saved: GeneratedVoucher[]) {
      const csv = ['Code', ...saved.map((voucher) => voucher.code.toUpperCase())].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `vouchers-${name.trim()}-${moment().format('YYYY-MM-DD-HHmmss')}.csv`
      link.click()
      URL.revokeObjectURL(url)
   }
}
